import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import type { MessageSuggestion, Partner } from "@expert/shared-types";
import { useEffect } from "react";
import { getRootDispatcher } from "@/analytics";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useDisplayCues } from "@/sales-tools/hooks/useDisplayCues";
import { useSessionId } from "@/sdk";

export const useTechCuesListener = (partner: Partner) => {
    const { displayCues, isLoading } = useDisplayCues(partner);
    const sessionId = useSessionId();

    useEffect(() => {
        const unsub = useDetectedCuesStore.persist.onFinishHydration((state) => {
            const hasExistingCues = !!state.displayCuesBySession[sessionId]?.length;
            if (!hasExistingCues && !isLoading) {
                useDetectedCuesStore.getState().setDefaultPartnerCues(sessionId, displayCues);
            }
        });
        return () => unsub();
    }, [sessionId, displayCues, isLoading]);

    useEffect(() => {
        const subscription = gaiaWsEventBus.on("gaia_ws_message-suggestion", ({ body }: GaiaWebSocketResponse) => {
            const gaiaSalesMessage = body as MessageSuggestion;

            if (gaiaSalesMessage.suggestion.cues) {
                const techCues = gaiaSalesMessage.suggestion.cues.filter(
                    (cue) => cue.type === "DEVICE" || cue.type === "SOFTWARE",
                );
                techCues.forEach((cue) => {
                    void getRootDispatcher().dispatchBusinessEvent("CustomerCueDetected", {
                        detectionSource: "GAIA",
                        cueType: cue.type,
                        cueName: cue.name,
                    });
                });

                useDetectedCuesStore.getState().processNewlyDetectedCue(gaiaSalesMessage.sessionGroupId, techCues);
                useOfferChecklistStore.getState().setData(gaiaSalesMessage.sessionGroupId, { DEVICE_CUE: true });
            }
        });
        return () => subscription();
    }, []);
};
